'use client';

import React, { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';

export default function OriginStorySection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef?.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) {setVisible(true);observer.disconnect();}},
      { threshold: 0.15 }
    );
    observer?.observe(el);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-20 md:py-28 relative overflow-hidden"
      aria-label="ForensicDesk origin story">
      
      {/* Ambient */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute top-[20%] right-[-5%] w-64 h-64 rounded-full"
          style={{ background: 'rgba(220, 38, 38, 0.04)', filter: 'blur(80px)' }} />
        
      </div>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* Left: Image */}
          <div
            className={`relative transition-all duration-1000 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            
            <div className="relative aspect-[4/3] overflow-hidden border border-border">
              <AppImage
                src="https://images.unsplash.com/photo-1680992046626-418f7e910589"
                alt="Dark server room with rows of illuminated rack servers, deep shadows, cool blue-tinted industrial lighting, steel walls"
                fill
                className="object-cover opacity-70"
                sizes="(max-width: 1024px) 100vw, 50vw" />
              
              {/* Scrim */}
              <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-background/10 to-transparent" aria-hidden="true" />
              {/* Classification overlay */}
              <div className="absolute bottom-4 left-4 glass-card px-3 py-2">
                <span className="bracket-tag text-muted-foreground">
                  [SECURE FACILITY // UNDISCLOSED LOCATION]
                </span>
              </div>
            </div>

            {/* Stat card */}
            <div className="absolute -bottom-6 -right-4 md:-right-8 glass-card-crimson border p-4 md:p-6 min-w-[140px]">
              <div className="text-3xl font-black text-foreground terminal-text mb-1">2021</div>
              <div className="bracket-tag text-muted-foreground">FOUNDED</div>
              <div className="text-xs text-muted-foreground mt-1 font-light">Private sector ops</div>
            </div>
          </div>

          {/* Right: Copy */}
          <div
            className={`flex flex-col justify-between gap-8 transition-all duration-1000 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-px bg-primary" />
                <span className="bracket-tag text-primary">[ORIGIN // CLASSIFIED BRIEF]</span>
              </div>

              <h2 className="text-3xl md:text-4xl font-black tracking-tighter uppercase text-foreground leading-tight mb-6">
                Built in the<br />
                <span className="text-gradient-crimson">Shadows.</span><br />
                Deployed in the Open.
              </h2>

              <p className="text-muted-foreground leading-relaxed mb-6 font-light">
                ForensicDesk was founded by a tight-knit group of veteran military intelligence
                and cyber-warfare specialists. For years, we operated exclusively in the
                private sector—shielding high-net-worth targets from complex digital extortion.
              </p>

              <p className="text-muted-foreground leading-relaxed font-light">
                The architecture of crypto fraud has evolved into industrialized syndicates:
                organized, jurisdictionally dispersed, and technically sophisticated.
                We are going public because the threat went global.
              </p>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-border">
              {[
              { val: '5+', label: 'Years Covert Ops' },
              { val: '18', label: 'Former Intelligence Officers' },
              { val: '4', label: 'Continents Active' }]?.
              map((s) =>
              <div key={s?.label} className="flex flex-col gap-1">
                  <div className="text-2xl font-black text-foreground terminal-text">{s?.val}</div>
                  <div className="text-xs text-muted-foreground font-medium uppercase tracking-wide leading-tight">{s?.label}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>);

}